/**
 * ============================================
 * CUSTOMER SUPPORT ACCORDION
 * Forgeline Motorsports - Shopify Theme
 * ============================================
 * 
 * Features:
 * - Smooth expand/collapse animation
 * - Keyboard accessibility (Enter/Space to toggle)
 * - ARIA attributes for screen readers
 * - Optional: Allow multiple open or single open mode
 * - URL hash support for direct linking to accordion items
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    allowMultipleOpen: true,  // Set to false for single-open mode (closes others when one opens)
    animationDuration: 300,   // Matches CSS transition speed
    scrollToItem: true,       // Scroll to item when opened via URL hash
    scrollOffset: 120         // Offset for fixed header when scrolling
  };

  /**
   * Initialize the accordion functionality
   */
  function initAccordion() {
    const accordion = document.getElementById('customerSupportAccordion');
    
    if (!accordion) {
      return;
    }

    const headers = accordion.querySelectorAll('.accordion-item__header');

    // Add click event listeners to all accordion headers
    headers.forEach(function(header) {
      header.addEventListener('click', function(event) {
        event.preventDefault();
        toggleAccordionItem(header, accordion);
      });

      // Add keyboard support
      header.addEventListener('keydown', function(event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleAccordionItem(header, accordion);
        }
      });
    });

    // Check for URL hash to open specific accordion item
    handleUrlHash(accordion);

    // Listen for hash changes
    window.addEventListener('hashchange', function() {
      handleUrlHash(accordion);
    });
  }

  /**
   * Toggle accordion item open/closed
   * @param {HTMLElement} header - The clicked header button
   * @param {HTMLElement} accordion - The parent accordion container
   */
  function toggleAccordionItem(header, accordion) {
    const isExpanded = header.getAttribute('aria-expanded') === 'true';
    const contentId = header.getAttribute('aria-controls');
    const content = document.getElementById(contentId);

    if (!content) {
      return;
    }

    // If single-open mode, close other items first
    if (!CONFIG.allowMultipleOpen && !isExpanded) {
      closeAllItems(accordion, header);
    }

    // Toggle the clicked item
    if (isExpanded) {
      closeItem(header, content);
    } else {
      openItem(header, content);
    }
  }

  /**
   * Open an accordion item
   * @param {HTMLElement} header - The header button
   * @param {HTMLElement} content - The content panel
   */
  function openItem(header, content) {
    header.setAttribute('aria-expanded', 'true');
    content.classList.add('is-open');
    
    // Set max-height for smooth animation
    content.style.maxHeight = content.scrollHeight + 'px';
    
    // Update URL hash (optional)
    // updateUrlHash(header);
  }

  /**
   * Close an accordion item
   * @param {HTMLElement} header - The header button
   * @param {HTMLElement} content - The content panel
   */
  function closeItem(header, content) {
    header.setAttribute('aria-expanded', 'false');
    content.classList.remove('is-open');
    content.style.maxHeight = '0';
  }

  /**
   * Close all accordion items except the specified one
   * @param {HTMLElement} accordion - The accordion container
   * @param {HTMLElement} exceptHeader - Header to exclude from closing (optional)
   */
  function closeAllItems(accordion, exceptHeader) {
    const headers = accordion.querySelectorAll('.accordion-item__header');
    
    headers.forEach(function(header) {
      if (header !== exceptHeader) {
        const contentId = header.getAttribute('aria-controls');
        const content = document.getElementById(contentId);
        
        if (content && header.getAttribute('aria-expanded') === 'true') {
          closeItem(header, content);
        }
      }
    });
  }

  /**
   * Handle URL hash to open specific accordion item
   * @param {HTMLElement} accordion - The accordion container
   */
  function handleUrlHash(accordion) {
    const hash = window.location.hash;
    
    if (!hash) {
      return;
    }

    // Try to find an accordion item with matching ID
    const targetContent = document.querySelector(hash);
    
    if (targetContent && targetContent.classList.contains('accordion-item__content')) {
      const header = accordion.querySelector('[aria-controls="' + targetContent.id + '"]');
      
      if (header && header.getAttribute('aria-expanded') !== 'true') {
        // Close others if in single-open mode
        if (!CONFIG.allowMultipleOpen) {
          closeAllItems(accordion, header);
        }
        
        // Open the target item
        openItem(header, targetContent);
        
        // Scroll to the item
        if (CONFIG.scrollToItem) {
          setTimeout(function() {
            const accordionItem = header.closest('.accordion-item');
            if (accordionItem) {
              const rect = accordionItem.getBoundingClientRect();
              const scrollTop = window.pageYOffset + rect.top - CONFIG.scrollOffset;
              
              window.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
              });
            }
          }, CONFIG.animationDuration);
        }
      }
    }
  }

  /**
   * Update URL hash when accordion item is opened
   * @param {HTMLElement} header - The header button
   */
  function updateUrlHash(header) {
    const contentId = header.getAttribute('aria-controls');
    
    if (contentId && history.pushState) {
      history.pushState(null, null, '#' + contentId);
    }
  }

  /**
   * Expand all accordion items (utility function)
   */
  function expandAll() {
    const accordion = document.getElementById('customerSupportAccordion');
    
    if (!accordion) {
      return;
    }

    const headers = accordion.querySelectorAll('.accordion-item__header');
    
    headers.forEach(function(header) {
      const contentId = header.getAttribute('aria-controls');
      const content = document.getElementById(contentId);
      
      if (content && header.getAttribute('aria-expanded') !== 'true') {
        openItem(header, content);
      }
    });
  }

  /**
   * Collapse all accordion items (utility function)
   */
  function collapseAll() {
    const accordion = document.getElementById('customerSupportAccordion');
    
    if (!accordion) {
      return;
    }

    const headers = accordion.querySelectorAll('.accordion-item__header');
    
    headers.forEach(function(header) {
      const contentId = header.getAttribute('aria-controls');
      const content = document.getElementById(contentId);
      
      if (content && header.getAttribute('aria-expanded') === 'true') {
        closeItem(header, content);
      }
    });
  }

  // Expose utility functions globally (optional)
  window.CustomerSupportAccordion = {
    expandAll: expandAll,
    collapseAll: collapseAll
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccordion);
  } else {
    initAccordion();
  }

  // Re-initialize on Shopify section load (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.target.querySelector('.customer-support-accordion')) {
        initAccordion();
      }
    });
  }

})();
